// The Take Off's rows: the column headings, a priced row (a surface or a
// paint system on one, its rates changeable for this option) and a section's extra rows.
import type { PaintChoice } from '@higgins/m2q-pricing';
import type { PricedSection, SectionInput } from '@higgins/m2q-pricing';
import { IN_FIELD_ORDER, moveToNextField } from '../lib/field-order';
import { NumberField } from './number-field';
import { formatMoney, formatPercent, formatQuantity, formatTwo } from './surface-format';

/**
 * The template's Take Off column headings, in its order, each with a fixed
 * width in rem (#395): the table lays out once, so a figure growing as you
 * type ($187.50 → $253.13) never moves the columns. Coats, Production Rate
 * and Spread Rate hold the inputs; Sell Price the widest figure.
 */
const TAKE_OFF_COLUMNS = [
  { label: 'Building/Area', rem: 18 },
  { label: 'Girth', rem: 3.5 },
  { label: 'Qty', rem: 4.5 },
  { label: 'Coats', rem: 4.5 },
  { label: 'Production Rate', rem: 4.5 },
  { label: 'Spread Rate', rem: 4.5 },
  { label: 'Material Rate', rem: 4 },
  { label: 'Hours', rem: 4.5 },
  { label: 'Wage Rate', rem: 4 },
  { label: 'Litres', rem: 4.5 },
  { label: 'Sell Price', rem: 6 },
  { label: 'Calc. Rate (m² rate)', rem: 4.5 },
  { label: 'Prep Rate', rem: 3.5 },
  { label: 'Prep hrs', rem: 4 },
];
export const COLUMN_COUNT = TAKE_OFF_COLUMNS.length;
/**
 * Fixed layout at least the columns' total width (74.5rem); wider screens share the spare room out,
 * narrower ones scroll the table sideways inside its box (#441). That box is a size container
 * (`@container`), so a whole-width row's content can be held to the part that shows (PINNED_ROW).
 */
export const TAKE_OFF_TABLE_CLASS = 'w-full min-w-[74.5rem] table-fixed text-sm';
/**
 * Building/Area stays put while the figures scroll under it (#441). A sticky cell needs its own
 * background or the scrolled figures show through; the inset shadow is its edge (a collapsed
 * border doesn't move with a sticky cell).
 */
export const PINNED_CELL = 'sticky left-0 z-10 shadow-[inset_-1px_0_0_#e2e8f0]';
/**
 * A row's spanning content (an area's heading, "Add hire…", an extra row's label) kept in the part of
 * the table that shows, wherever it is scrolled to; it never leaves its own cell, so an extra row's
 * label can't cover its Hours figure.
 */
export const PINNED_ROW = 'sticky left-2 max-w-[calc(100cqw-1rem)]';
/** Columns before Hours, which an extra row's label spans. */
const LABEL_SPAN = 7;

export const NUM = 'px-1.5 py-1 text-right tabular-nums whitespace-nowrap outdoor:text-base outdoor:font-[500]';
/** A value this option changed: the template's yellow. */
export const CHANGED = 'bg-amber-50';

export function TakeOffHeader() {
  return (
    <>
      <colgroup>
        {TAKE_OFF_COLUMNS.map((c) => (
          <col key={c.label} style={{ width: `${c.rem}rem` }} />
        ))}
      </colgroup>
      <thead>
        <tr className="text-xs text-slate-500">
          {TAKE_OFF_COLUMNS.map((c, i) => (
            <th key={c.label} className={`px-1.5 py-1.5 align-bottom font-normal leading-tight ${i === 0 ? `${PINNED_CELL} bg-white text-left` : 'text-right'}`}>
              {c.label}
            </th>
          ))}
        </tr>
      </thead>
    </>
  );
}

/** An extra row's figure cells after its label: hours, litres and sell, then the rest of the columns blank. */
export function ExtraFigures({ hours, litres, sell }: { hours?: number; litres?: number; sell?: number }) {
  return (
    <>
      <td className={NUM}>{hours === undefined ? '' : formatTwo(hours)}</td>
      <td />
      <td className={NUM}>{litres === undefined ? '' : formatTwo(litres)}</td>
      <td className={NUM}>{sell === undefined ? '' : sell ? formatMoney(sell) : '—'}</td>
      <td colSpan={COLUMN_COUNT - LABEL_SPAN - 4} />
    </>
  );
}

/** A section's extra row: label + detail across the surface columns, then hours, litres and sell. */
export function ExtraRow({ label, detail, hours, litres, sell }: { label: string; detail?: React.ReactNode; hours?: number; litres?: number; sell?: number }) {
  return (
    <tr className="text-slate-700">
      <td colSpan={LABEL_SPAN} className="px-2 py-1">
        <div className={`${PINNED_ROW} w-fit`}>
          {label} {detail && <span className="text-xs text-slate-500">{detail}</span>}
        </div>
      </td>
      <ExtraFigures hours={hours} litres={litres} sell={sell} />
    </tr>
  );
}

/** A share-of-hours row (overtime, foreman, abseil): hours at its loading rate. */
export function ShareRow({ label, share, hours, sell }: { label: string; share: number; hours: number; sell: number }) {
  return <ExtraRow label={label.replace('{p}', formatPercent(share))} hours={hours} sell={sell} />;
}

/** What the desk can type on a Take Off row: a surface's coats, rate and spread; a paint system's prep too. */
export type RowSetting = 'coats' | 'rate' | 'spread' | 'prep';

interface WorkRowProps<S extends RowSetting> {
  /** The first cell: the row's name and its controls. */
  label: React.ReactNode;
  priced: PricedSection['surfaces'][number];
  input: SectionInput['surfaces'][number];
  /** This option's typed values (yellow). */
  values: Partial<Record<RowSetting, number>>;
  /** What each value is without them: the placeholder when cleared. */
  placeholders: Record<RowSetting, number>;
  editable: readonly S[];
  onSetting: (setting: S, value: number | null) => void;
  /** A paint system: its girth and quantity are the surface's above, so they are greyed. */
  shared?: boolean;
}

/** One priced row of the Take Off: a surface, or a paint system on one (#412). */
export function WorkRow<S extends RowSetting>({ label, priced: s, input, values, placeholders, editable, onSetting, shared = false }: WorkRowProps<S>) {
  const isEditable = (setting: RowSetting): setting is S => (editable as readonly RowSetting[]).includes(setting);
  const cell = (setting: RowSetting, shown: React.ReactNode) =>
    isEditable(setting) ? (
      <td className={`px-1 py-0.5 ${values[setting] !== undefined ? CHANGED : ''}`}>
        <NumberField compact value={values[setting]} placeholder={String(placeholders[setting])} onChange={(v) => onSetting(setting, v)} label={`${s.name} ${setting}`} className="w-14" />
      </td>
    ) : (
      <td className={NUM}>{shown}</td>
    );
  return (
    // A wash-only area's surfaces stay measured but aren't priced (#510): shown greyed.
    <tr className={`${shared ? 'border-t border-dashed border-slate-100' : 'border-t border-slate-100'} ${input.unpriced ? 'text-slate-400' : ''}`}>
      {/* The name cell clips its own content, so nothing in it can spill over Girth (#412). */}
      <td className={`${PINNED_CELL} overflow-hidden bg-white px-2 py-1`}>{label}</td>
      <td className={`${NUM} ${shared ? 'text-slate-400' : ''}`}>{formatTwo(input.girth)}</td>
      <td className={`${NUM} ${shared ? 'text-slate-400' : 'bg-sky-50'}`}>{formatQuantity(input.quantity)}</td>
      {cell('coats', input.coats)}
      {cell('rate', input.rate)}
      {cell('spread', input.spread)}
      <td className={NUM}>{formatTwo(s.materialRate)}</td>
      <td className={`${NUM} font-semibold`}>{formatTwo(s.hours)}</td>
      <td className={NUM}>{formatTwo(s.wageRate)}</td>
      <td className={NUM}>{formatTwo(s.litres)}</td>
      <td className={`${NUM} font-semibold`}>{formatMoney(s.sell)}</td>
      <td className={NUM}>{formatTwo(s.m2Rate)}</td>
      {cell('prep', input.prep)}
      <td className={NUM}>{formatTwo(s.prepHours)}</td>
    </tr>
  );
}

/** A row's paint, from the option's paint list; yellow once it isn't the option's first. `width`: a fixed Tailwind width, so the name beside it keeps its room. */
export function PaintSelect({ value, paints, changed, label, onChange, width = 'w-32' }: { value: string; paints: PaintChoice[]; changed: boolean; label: string; onChange: (key: string) => void; width?: string }) {
  if (paints.length < 2) return null;
  return (
    <select {...IN_FIELD_ORDER} onKeyDown={(e) => moveToNextField(e)} value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} className={`h-6 ${width} shrink-0 rounded border border-slate-300 px-0.5 text-xs ${changed ? CHANGED : 'bg-white'}`}>
      {paints.map((p) => (
        <option key={p.key} value={p.key}>
          {p.name}
        </option>
      ))}
    </select>
  );
}

/** A row for something this option leaves out, with the way to put it back. */
export function LeftOutRow({ label, onPutBack }: { label: string; onPutBack: () => void }) {
  return (
    <tr className="text-slate-400">
      <td colSpan={COLUMN_COUNT} className="px-2 py-1 text-sm">
        <div className={PINNED_ROW}>
          <span className="line-through">{label}</span> — left out of this option{' '}
          <button type="button" onClick={onPutBack} className="text-primary underline">
            Put back
          </button>
        </div>
      </td>
    </tr>
  );
}
